import { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { WarningIcon as Warning } from "@phosphor-icons/react";

const Ctx = createContext(null);

export function UnsavedChangesProvider({ children }) {
  const navigate = useNavigate();
  const guardRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);

  const setGuard = useCallback((g) => { guardRef.current = g; }, []);
  const clearGuard = useCallback((g) => { if (!g || guardRef.current === g) guardRef.current = null; }, []);

  const guardedNavigate = useCallback((to) => {
    const g = guardRef.current;
    if (g && g.isDirty()) setPending({ to });
    else navigate(to);
  }, [navigate]);

  const stay = () => setPending(null);
  const discard = () => { const t = pending?.to; setPending(null); navigate(t); };
  const saveAndLeave = async () => {
    const g = guardRef.current; const t = pending?.to;
    if (!g) { setPending(null); navigate(t); return; }
    setSaving(true);
    let ok = false;
    try { ok = await g.save(); } finally { setSaving(false); }
    if (ok) { setPending(null); navigate(t); }
  };

  return (
    <Ctx.Provider value={{ setGuard, clearGuard, guardedNavigate }}>
      {children}
      <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <AlertDialogContent className="rounded-sm" data-testid="global-leave-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2"><Warning size={20} className="text-amber-500" /> Modifiche non salvate</AlertDialogTitle>
            <AlertDialogDescription>Ci sono modifiche non salvate in questa scheda. Vuoi salvarle prima di uscire?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel className="rounded-sm" data-testid="global-leave-stay-btn" onClick={stay}>Resta nella pagina</AlertDialogCancel>
            <Button variant="outline" className="rounded-sm text-destructive border-destructive/40 hover:bg-destructive/5" data-testid="global-leave-discard-btn" onClick={discard}>Esci senza salvare</Button>
            <Button className="rounded-sm" data-testid="global-leave-save-btn" onClick={saveAndLeave} disabled={saving}>{saving ? "Salvataggio…" : "Salva ed esci"}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Ctx.Provider>
  );
}

export function useGuardedNavigate() {
  const ctx = useContext(Ctx);
  return ctx ? ctx.guardedNavigate : null;
}

// dirty: boolean, save: async () => boolean (true on success)
export function useUnsavedGuard(dirty, save) {
  const ctx = useContext(Ctx);
  const dirtyRef = useRef(dirty);
  const saveRef = useRef(save);
  dirtyRef.current = dirty;
  saveRef.current = save;

  useEffect(() => {
    if (!ctx) return;
    const g = { isDirty: () => !!dirtyRef.current, save: () => saveRef.current() };
    ctx.setGuard(g);
    return () => ctx.clearGuard(g);
  }, [ctx]);

  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  return ctx ? ctx.guardedNavigate : null;
}
